const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const SESSION_KEY = "ma-soi:cloudflare-session";

export interface GuestSession {
  token: string;
  uid: string;
}

let sessionPromise: Promise<GuestSession> | null = null;

export function requestId(): string {
  return crypto.randomUUID();
}

export async function ensureSession(forceNew = false): Promise<GuestSession> {
  if (forceNew) {
    localStorage.removeItem(SESSION_KEY);
    sessionPromise = null;
  }
  const saved = localStorage.getItem(SESSION_KEY);
  if (saved && !forceNew) {
    try {
      return JSON.parse(saved) as GuestSession;
    } catch {
      localStorage.removeItem(SESSION_KEY);
    }
  }
  if (!sessionPromise) {
    sessionPromise = fetch(`${API_BASE}/api/session`, {
      method: "POST",
      headers: { "content-type": "application/json" },
    })
      .then(async (response) => {
        const data = await response.json() as GuestSession & { error?: string };
        if (!response.ok) throw new Error(data.error ?? "Không thể tạo phiên đăng nhập.");
        localStorage.setItem(SESSION_KEY, JSON.stringify(data));
        return data;
      })
      .finally(() => {
        sessionPromise = null;
      });
  }
  return sessionPromise;
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  retrySession = true,
): Promise<T> {
  const session = await ensureSession();
  const headers = new Headers(init.headers);
  headers.set("authorization", `Bearer ${session.token}`);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const data = await response.json().catch(() => ({})) as T & { error?: string };
  if (response.status === 401 && retrySession) {
    await ensureSession(true);
    return apiRequest<T>(path, init, false);
  }
  if (!response.ok) throw new Error(data.error ?? "Yêu cầu không thành công.");
  return data;
}

export async function publicRequest<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`);
  const data = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(data.error ?? "Yêu cầu không thành công.");
  return data;
}

export async function callRoomCommand<T = { ok: true }>(
  roomId: string,
  command: string,
  payload: Record<string, unknown> = {},
): Promise<T> {
  return apiRequest<T>(`/api/rooms/${roomId}/command`, {
    method: "POST",
    body: JSON.stringify({ requestId: requestId(), command, payload }),
  });
}

export async function roomWebSocket(roomId: string): Promise<WebSocket> {
  const session = await ensureSession();
  const base = API_BASE
    ? new URL(API_BASE, window.location.href)
    : new URL(window.location.origin);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = `/api/rooms/${roomId}/ws`;
  base.search = "";
  base.hash = "";
  return new WebSocket(base, ["ma-soi", session.token]);
}

export function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) return String(error.message);
  return "Đã xảy ra lỗi. Hãy thử lại.";
}

export function saveResumeToken(roomId: string, token: string): void {
  localStorage.setItem(`ma-soi:resume:${roomId}`, token);
}

export function removeResumeToken(roomId: string): void {
  localStorage.removeItem(`ma-soi:resume:${roomId}`);
}

export function getResumeToken(roomId: string): string | null {
  return localStorage.getItem(`ma-soi:resume:${roomId}`);
}

export function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.ceil(totalSeconds));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
}
